import {CouponInfoSheet} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {ProminentBannerSection_CouponSlider} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {
  COLORS,
  CompactSlideInContainer,
  Flyout,
  HorizontalList,
  HorizontalListWrapper,
  ModalDialog,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React, {useState} from 'react';
import sc from 'styled-components';
import {CouponCard} from '../../../../../features/CouponCard/CouponCard';
import {AppOnlyInfoFlyout} from '../../../../../features/CouponCard/components/AppOnlyInfoFlyout';
import {CouponInfoContentPopper} from '../../../../../features/Coupons/CouponInfoContentPopper/CouponInfoContentPopper';
import {getCouponPromotionId} from '../../../../../features/Coupons/getCouponPromotionId';
import {useActivatedCouponState} from '../../../../../features/Coupons/useActivatedCouponState';
import {useTrackCoupons} from '../utils/useTrackCoupons';
import {CouponSliderHeader} from './CouponSliderHeader';
import {useScrollToActiveCoupon} from './utils/useScrollToActiveCoupon';

interface CouponSliderProps {
  className?: string;
  couponSlider: ProminentBannerSection_CouponSlider;
  productId: number;
  flatBorders?: boolean;
  removeNegativeMarginTop?: boolean;
}

export const CouponSlider: React.FC<CouponSliderProps> = ({
  className,
  couponSlider,
  productId,
  flatBorders,
  removeNegativeMarginTop,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const {activatedCoupon, couponPromotionId, headline, unlockedCouponHeadline} =
    useActivatedCouponState(productId);
  const couponCardDataTestid = 'couponCard';
  const [infoIconContent, setInfoIconContent] = useState<CouponInfoSheet>();

  const handleInfoIconClick = (couponInfo?: CouponInfoSheet) => {
    setInfoIconContent(couponInfo);
  };

  const {featureTrackingClickCallback, trackingRef} =
    useTrackCoupons(productId);

  const {scrollWrapperRef} = useScrollToActiveCoupon({
    coupons: couponSlider.coupons,
    couponPromotionId,
  });

  const handleOnToggle = (value: boolean) => {
    setIsOpen(value);

    if (value) {
      featureTrackingClickCallback();
    }
  };

  return (
    <>
      <StyledCompactSlideInContainer
        className={className}
        ref={trackingRef}
        data-testid="couponSlider"
        data-is-open={isOpen}
        removeNegativeMarginTop={removeNegativeMarginTop}
        HeaderContent={
          <CouponSliderHeader
            activatedCoupon={activatedCoupon}
            couponSlider={couponSlider}
            isActive={Boolean(activatedCoupon)}
            isOpen={isOpen}
            flatBorders={flatBorders}
            headline={unlockedCouponHeadline || headline}
            fullWidthHeadline={Boolean(unlockedCouponHeadline)}
          />
        }
        onToggle={handleOnToggle}
      >
        <StyledHorizontalList
          ref={scrollWrapperRef}
          $centered={couponSlider.coupons.length === 1}
        >
          {couponSlider.coupons.map(coupon => (
            <StyledCouponCard
              key={getCouponPromotionId(coupon)}
              coupon={coupon}
              productId={productId}
              onInfoIconClick={handleInfoIconClick}
              isDisplayMode={false}
            />
          ))}
        </StyledHorizontalList>
      </StyledCompactSlideInContainer>
      <CouponInfoContentPopper
        LayoutComponent={process.env.STACK === 'mobile' ? Flyout : ModalDialog}
        data-testid={couponCardDataTestid}
        couponInfo={infoIconContent}
      />
      <AppOnlyInfoFlyout data-testid={couponCardDataTestid} />
    </>
  );
};

const StyledCompactSlideInContainer = sc(CompactSlideInContainer)`
  position: relative;
  z-index: ${Z_INDEXES.ADP_MWEB_COUPON_SLIDER};

  &[data-is-open='true'] {
    background-color: transparent;
  }
`;

const StyledHorizontalList = linariaStyled(HorizontalList)<{
  $centered?: boolean;
}>`
  background-color: ${COLORS.BackgroundCoupon};
  justify-content: center;

  ${HorizontalListWrapper} {
    width: 100%;
    gap: 8px;
    padding: 16px;

    &:after {
      display: none;
    }

    justify-content: ${({$centered}) => ($centered ? 'center' : 'flex-start')};
  }
`;

const StyledCouponCard = sc(CouponCard)`
  flex: 0 0 311px;
`;
